
import React from 'react';
import { SimulationConfig } from '@/types';
import { SmartInput } from '@/studio/SmartInput';
import { Toggle } from '@/studio/Toggle';

interface SimulationEditorProps {
    config: SimulationConfig;
    setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
    sim: any;
    simIdx: number;
}

export const SimulationEditor: React.FC<SimulationEditorProps> = ({ config, setConfig, sim, simIdx }) => {
    return (
        <div id={`sim-edit-${sim.id}`} className="pt-6 mt-4 border-t border-white/5 space-y-8 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="flex items-center justify-between mb-8 pb-4 border-b border-white/5">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-blue-500/20 flex items-center justify-center border border-blue-500/30">
                        <i className="fa-solid fa-sliders text-blue-400 text-lg"></i>
                    </div>
                    <div>
                        <h5 className="text-sm font-black text-white uppercase tracking-[0.2em]">Settings</h5>
                        <p className="text-[10px] text-white/40 font-bold uppercase tracking-widest">{sim.name}</p>
                    </div>
                </div>
            </div>
              
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-6">
                <div className="space-y-5">
                    <div className="flex items-center gap-3">
                        <span className="h-px flex-1 bg-gradient-to-r from-blue-500/50 to-transparent"></span>
                        <p className="text-[9px] text-blue-400 font-black uppercase tracking-[0.25em]">Physics</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-4">
                            <p className="text-[9px] text-white/40 font-black uppercase tracking-widest px-1">Center Offset (px)</p>
                            <div className="grid grid-cols-2 gap-3">
                                <SmartInput 
                                    label="X Offset" 
                                    value={sim.settings?.rhythmicSimCenter?.x ?? 0} 
                                    onChange={(v: number) => {
                                        const newSims = [...config.rhythmicSimulations];
                                        const currentSettings = sim.settings || {};
                                        const currentCenter = currentSettings.rhythmicSimCenter || { x: 0, y: 0 };
                                        newSims[simIdx] = { ...sim, settings: { ...currentSettings, rhythmicSimCenter: { ...currentCenter, x: v } } };
                                        setConfig({ ...config, rhythmicSimulations: newSims });
                                    }}
                                    min={-2000} max={2000} darkMode={config.darkMode}
                                />
                                <SmartInput 
                                    label="Y Offset" 
                                    value={sim.settings?.rhythmicSimCenter?.y ?? 0} 
                                    onChange={(v: number) => {
                                        const newSims = [...config.rhythmicSimulations];
                                        const currentSettings = sim.settings || {};
                                        const currentCenter = currentSettings.rhythmicSimCenter || { x: 0, y: 0 };
                                        newSims[simIdx] = { ...sim, settings: { ...currentSettings, rhythmicSimCenter: { ...currentCenter, y: v } } };
                                        setConfig({ ...config, rhythmicSimulations: newSims });
                                    }}
                                    min={-2000} max={2000} darkMode={config.darkMode}
                                />
                            </div>
                        </div>
                        <div className="space-y-4">
                            <p className="text-[9px] text-white/40 font-black uppercase tracking-widest px-1">Container Size</p>
                            <SmartInput 
                                label="Radius" 
                                value={sim.settings?.containerRadius ?? config.containerRadius} 
                                onChange={(v: number) => {
                                    const newSims = [...config.rhythmicSimulations];
                                    newSims[simIdx] = { ...sim, settings: { ...(sim.settings || {}), containerRadius: v } };
                                    setConfig({ ...config, rhythmicSimulations: newSims });
                                }}
                                min={50} max={1000} darkMode={config.darkMode}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                            <p className="text-[9px] text-white/40 font-black uppercase tracking-widest">Shape & Rotation</p>
                            <div className="flex gap-2 items-end">
                                <div className="flex-1 space-y-1.5">
                                    <label className="text-[8px] uppercase font-black tracking-widest opacity-40">Shape</label>
                                    <button 
                                        onClick={() => {
                                            const newSims = [...config.rhythmicSimulations];
                                            const shapes: any[] = ['CIRCLE', 'SQUARE', 'TRIANGLE', 'HEXAGON', 'OCTAGON', 'STAR_5', 'HEART'];
                                            const current = sim.settings?.containerShape || config.containerShape;
                                            const next = shapes[(shapes.indexOf(current) + 1) % shapes.length];
                                            newSims[simIdx] = { ...sim, settings: { ...(sim.settings || {}), containerShape: next } };
                                            setConfig({ ...config, rhythmicSimulations: newSims });
                                        }}
                                        className="w-full px-3 py-2 bg-white/5 rounded-xl text-[10px] font-bold text-white hover:bg-white/10 border border-white/5 transition-all text-left flex justify-between items-center"
                                    >
                                        {sim.settings?.containerShape ?? config.containerShape}
                                        <i className="fa-solid fa-chevron-right text-[8px] opacity-30"></i>
                                    </button>
                                </div>
                                <div className="w-24">
                                    <SmartInput 
                                        label="Rot" 
                                        value={sim.settings?.containerRotation ?? config.containerRotation} 
                                        onChange={(v: number) => {
                                            const newSims = [...config.rhythmicSimulations];
                                            newSims[simIdx] = { ...sim, settings: { ...(sim.settings || {}), containerRotation: v } };
                                            setConfig({ ...config, rhythmicSimulations: newSims });
                                        }}
                                        min={0} max={360} darkMode={config.darkMode}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <p className="text-[9px] text-white/40 font-black uppercase tracking-widest">Borders</p>
                            <div className="flex gap-3 items-end">
                                <div className="flex-1">
                                    <SmartInput 
                                        label="Count" 
                                        value={sim.settings?.numOuterBorders ?? config.numOuterBorders} 
                                        onChange={(v: number) => {
                                            const newSims = [...config.rhythmicSimulations];
                                            newSims[simIdx] = { ...sim, settings: { ...(sim.settings || {}), numOuterBorders: v } };
                                            setConfig({ ...config, rhythmicSimulations: newSims });
                                        }}
                                        min={0} max={10} darkMode={config.darkMode}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[8px] uppercase font-black tracking-widest opacity-40">Color</label>
                                    <div className="relative w-10 h-10 rounded-xl overflow-hidden border border-white/10">
                                        <input 
                                            type="color" 
                                            value={sim.settings?.borderColor ?? config.borderColor}
                                            onChange={(e) => {
                                                const newSims = [...config.rhythmicSimulations];
                                                newSims[simIdx] = { ...sim, settings: { ...(sim.settings || {}), borderColor: e.target.value } };
                                                setConfig({ ...config, rhythmicSimulations: newSims });
                                            }}
                                            className="absolute inset-[-50%] w-[200%] h-[200%] cursor-pointer"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center gap-2">
                        <span className="h-px flex-1 bg-gradient-to-r from-emerald-500/50 to-transparent"></span>
                        <p className="text-[10px] text-emerald-400 font-black uppercase tracking-[0.15em]">Ball Properties</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                            <p className="text-[9px] text-white/40 font-black uppercase tracking-widest">Size & Color</p>
                            <div className="flex gap-3 items-end">
                                <div className="flex-1">
                                    <SmartInput 
                                        label="Radius" 
                                        value={sim.settings?.ballRadius ?? config.ballRadius} 
                                        onChange={(v: number) => {
                                            const newSims = [...config.rhythmicSimulations];
                                            newSims[simIdx] = { ...sim, settings: { ...(sim.settings || {}), ballRadius: v } };
                                            setConfig({ ...config, rhythmicSimulations: newSims });
                                        }}
                                        min={2} max={100} darkMode={config.darkMode}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[8px] uppercase font-black tracking-widest opacity-40">Color</label>
                                    <div className="relative w-10 h-10 rounded-xl overflow-hidden border border-white/10">
                                        <input 
                                            type="color" 
                                            value={sim.settings?.ballColor ?? config.ballColor}
                                            onChange={(e) => {
                                                const newSims = [...config.rhythmicSimulations];
                                                newSims[simIdx] = { ...sim, settings: { ...(sim.settings || {}), ballColor: e.target.value } };
                                                setConfig({ ...config, rhythmicSimulations: newSims });
                                            }}
                                            className="absolute inset-[-50%] w-[200%] h-[200%] cursor-pointer"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <p className="text-[9px] text-white/40 font-black uppercase tracking-widest">Visual Style</p>
                            <div className="flex gap-3 items-end">
                                <div className="flex-1 space-y-1.5">
                                    <label className="text-[8px] uppercase font-black tracking-widest opacity-40">Style</label>
                                    <button 
                                        onClick={() => {
                                            const newSims = [...config.rhythmicSimulations];
                                            const styles: any[] = ['SHINY', 'GLOW', 'GLASS', 'METAL', 'NEON', 'NONE', 'CHROME', 'MATTE', 'GHOST', 'CRYSTAL', 'WIREFRAME', 'HOLOGRAPHIC', 'PLASMA', 'FROSTED', 'PEARL', 'IRIDESCENT', 'LAVA', 'ELECTRIC', 'CARBON', 'WOOD', 'MARBLE', 'GOLD', 'SILVER', 'COPPER', 'RUBY', 'EMERALD', 'SAPPHIRE'];
                                            const current = sim.settings?.ballStyle || config.ballStyle;
                                            const next = styles[(styles.indexOf(current) + 1) % styles.length];
                                            newSims[simIdx] = { ...sim, settings: { ...(sim.settings || {}), ballStyle: next } };
                                            setConfig({ ...config, rhythmicSimulations: newSims });
                                        }}
                                        className="w-full px-3 py-2 bg-white/5 rounded-xl text-[10px] font-bold text-white hover:bg-white/10 border border-white/5 transition-all text-left flex justify-between items-center"
                                    >
                                        {sim.settings?.ballStyle ?? config.ballStyle}
                                        <i className="fa-solid fa-chevron-right text-[8px] opacity-30"></i>
                                    </button>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[8px] uppercase font-black tracking-widest opacity-40">RGB</label>
                                    <div className="flex items-center justify-center bg-white/5 rounded-xl px-2 h-10 border border-white/5">
                                        <Toggle 
                                            active={sim.settings?.rgbLighting ?? config.rgbLighting} 
                                            onClick={() => {
                                                const newSims = [...config.rhythmicSimulations];
                                                newSims[simIdx] = { ...sim, settings: { ...(sim.settings || {}), rgbLighting: !(sim.settings?.rgbLighting ?? config.rgbLighting) } };
                                                setConfig({ ...config, rhythmicSimulations: newSims });
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
